import { prisma, type Prisma, ResourceType, ActionType } from "client";
import { updateTicket } from "services/ticket";

// READ

export const getTicketCategory = async (userId: string, id: string) => {
  try {
    const ticketCategory = await prisma.ticketCategory.findUnique({
      where: { id },
      include: { tickets: true },
    });
    return ticketCategory;
  } catch (error: any) {
    return error;
  }
};
export const getAllTicketCategory = async (userId: string, id: string) => {
  try {
    const ticketCategory = await prisma.ticketCategory.findMany({
      where: { eventId: id },
      include: {
        tickets: true,
        dtcmSections: {
          include: {
            category: true,
          },
        },
      },
      orderBy: { id: "asc" },
    });
    return ticketCategory;
  } catch (error: any) {
    return error;
  }
};

// CREATE
export const createTicketCategory = async (
  userId: string,
  eventId: string,
  ticketCategoryPayload: Prisma.TicketCategoryCreateInput
) => {
  try {
    if (
      ticketCategoryPayload.isSharedQuantity &&
      (!ticketCategoryPayload?.quantity || ticketCategoryPayload.quantity <= 0)
    ) {
      throw new Error("Quantity must be greater than 0");
    }
    if (
      !ticketCategoryPayload.isSharedQuantity &&
      ticketCategoryPayload?.quantity
    ) {
      throw new Error("Quantity must be equal 0");
    }
    const ticketCategory = await prisma.ticketCategory.create({
      data: {
        ...ticketCategoryPayload,
        event: {
          connect: { id: eventId },
        },
      },
    });

    return ticketCategory;
  } catch (err) {
    throw err;
  }
};

// UPDATE
export const updateTicketCategory = async (
  userId: string,
  id: string,
  updatedData: Prisma.TicketCategoryUpdateInput,
  ticketIds?: { id: string; quantity: number }[]
) => {
  try {
    let tickets = {};
    if (ticketIds) {
      const ticketsInOtherCategories = await prisma.ticket.findMany({
        where: {
          id: {
            in: ticketIds.map((ticket) => ticket.id),
          },
          NOT: [
            {
              categoryId: id,
            },
          ],
        },
      });
      if (ticketsInOtherCategories.length > 0) {
        throw new Error(
          "One or more tickets are already associated with another category."
        );
      }
      const existingTicketCategory = await prisma.ticketCategory.findUnique({
        where: {
          id,
        },
        include: {
          tickets: true,
        },
      });

      let existingTicketIds: string[] = [];
      let ticketIdsToDisconnect: string[] = [];
      let ticketIdsToConnect: string[] = [];

      if (existingTicketCategory) {
        existingTicketIds = existingTicketCategory.tickets.map(
          (ticket) => ticket.id
        );
      }

      if (existingTicketIds?.length > 0) {
        ticketIdsToDisconnect = existingTicketIds.filter(
          (ticketId) => !ticketIds.some((ticket) => ticket.id === ticketId)
        );
      }

      if (ticketIds?.length > 0) {
        ticketIdsToConnect = ticketIds
          .map((ticket) => ticket.id)
          .filter((ticketId) => !existingTicketIds.includes(ticketId));

        if (updatedData?.isSharedQuantity)
          ticketIds.map(async (quantity: any) => {
            await updateTicket(
              userId,
              quantity.id,
              {
                quantity: Number(updatedData.quantity),
                isQuantitySharedWithGroup: true,
              },
              []
            );
          });
        else
          ticketIds.map(async (quantity: any) => {
            await updateTicket(
              userId,
              quantity.id,
              {
                quantity: quantity.quantity,
                isQuantitySharedWithGroup: false,
              },
              []
            );
          });
      }
      tickets = {
        disconnect: ticketIdsToDisconnect.map((ticketId) => ({
          id: ticketId,
        })),
        connect: ticketIdsToConnect.map((ticketId) => ({ id: ticketId })),
      };
    }

    const ticketCategory = await prisma.ticketCategory.update({
      where: {
        id,
      },
      data: {
        ...updatedData,
        tickets: { ...tickets },
      },
    });
    return ticketCategory;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// DELETE
export const deleteTicketCategory = async (userId: string, id: string) => {
  try {
    const ticketCategory = await prisma.ticketCategory.findUnique({
      where: {
        id,
      },
      include: {
        tickets: true,
      },
    });

    if (!ticketCategory) {
      throw new Error(`Event Group with ID ${id} not found.`);
    }

    const ticketsIds = ticketCategory.tickets.map((ticket) => ticket.id);
    await prisma.ticketCategory.update({
      where: {
        id,
      },
      data: {
        tickets: {
          disconnect: ticketsIds.map((ticketsId) => ({ id: ticketsId })),
        },
      },
    });
    await prisma.ticketCategory.delete({
      where: {
        id,
      },
    });
    return ticketCategory;
  } catch (error: any) {
    return error;
  }
};
